import "./FilterPresetButtons.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import { Select, TextInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useSavedViews } from "pages/finance/hooks/useSavedViews";
import React, { useState } from "react";
import type { SavedView, SavedViewScope } from "services/saved-view-api";

type FinanceFilterPresetButtonsProps = {
  currentUrl: string;
  onLoadPreset: (preset: SavedView) => void;
  disabled?: boolean;
  currentPreset?: SavedView | null;
};

// component that shows the filter preset buttons Save, Load)
const FinanceFilterPresetButtons: React.FC<FinanceFilterPresetButtonsProps> = ({
  currentUrl,
  onLoadPreset,
  disabled = false,
  currentPreset = null,
}) => {
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState<SavedView | null>(null);
  const [saveName, setSaveName] = useState("");
  const [saveDescription, setSaveDescription] = useState("");
  const [saveScope, setSaveScope] = useState<SavedViewScope>("USER");
  const [saveMode, setSaveMode] = useState<"new" | "update">("new");

  const {
    financePresets,
    isLoadingSavedViews,
    isCreating,
    isDeleting,
    isUpdating,
    saveAsPreset,
    updatePreset,
    deletePreset,
  } = useSavedViews();

  const scopeOptions: OptionType[] = [
    { label: "Personal (Only Me)", value: "USER" },
    { label: "Organization (Everyone)", value: "ORGANIZATION" },
  ];

  const handleDeletePreset = (preset: SavedView) => {
    setPresetToDelete(preset);
    setShowDeleteModal(true);
  };

  const confirmDeletePreset = async () => {
    if (!presetToDelete) return;

    try {
      await deletePreset(presetToDelete.id);
      setShowDeleteModal(false);
      setPresetToDelete(null);
    } catch (error) {
      console.error("Failed to delete preset:", error);
    }
  };

  const handleSaveOrUpdate = async () => {
    if (!saveName.trim()) return;

    try {
      if (
        saveMode === "update" &&
        currentPreset &&
        saveName.trim() === currentPreset.name
      ) {
        // update existing preset
        const updatedPreset = await updatePreset(currentPreset.id, {
          name: saveName.trim(),
          description: saveDescription.trim() || undefined,
          scope: saveScope,
          shareableUrl: currentUrl,
        });

        if (updatedPreset) {
          onLoadPreset(updatedPreset);
        }
      } else {
        // create new preset
        const newPreset = await saveAsPreset({
          name: saveName.trim(),
          description: saveDescription.trim() || undefined,
          scope: saveScope,
          shareableUrl: currentUrl,
        });

        if (newPreset) {
          onLoadPreset(newPreset);
        }
      }

      // reset form and close modal
      setSaveName("");
      setSaveDescription("");
      setSaveScope("USER");
      setSaveMode("new");
      setShowSaveModal(false);
    } catch (error) {
      console.error("Failed to save preset:", error);
    }
  };

  return (
    <>
      <Button
        className="header-action-trio-button"
        onClick={() => {
          if (disabled) return;

          // pre-populate form if preset is loaded
          if (currentPreset) {
            setSaveName(currentPreset.name);
            setSaveDescription(currentPreset.description || "");
            setSaveScope(currentPreset.scope);
            setSaveMode("update");
          } else {
            setSaveName("");
            setSaveDescription("");
            setSaveScope("USER");
            setSaveMode("new");
          }
          setShowSaveModal(true);
        }}
        title={
          currentPreset
            ? `Update "${currentPreset.name}" or Save New`
            : "Save as Preset"
        }
        disabled={disabled}
      >
        <span className="material-icons">save</span>
        <span>Save</span>
      </Button>
      <Button
        className="header-action-trio-button"
        onClick={() =>
          !(disabled || isLoadingSavedViews) && setShowLoadModal(true)
        }
        title={`Load Preset (${financePresets.length} available)`}
        disabled={disabled || isLoadingSavedViews}
      >
        <span className="material-icons">folder_open</span>
        <span>Load</span>
      </Button>

      {/* save or update preset modal */}
      <KaboodleModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        footer={
          <div className="flex jc-c">
            <Button
              type="button"
              onClick={handleSaveOrUpdate}
              disabled={isCreating || isUpdating || !saveName.trim()}
            >
              {isCreating || isUpdating
                ? saveMode === "update" && saveName === currentPreset?.name
                  ? "Updating..."
                  : "Saving..."
                : saveMode === "update" && saveName === currentPreset?.name
                  ? "Update Preset"
                  : "Save Preset"}
            </Button>
            <Button
              type="button"
              onClick={() => setShowSaveModal(false)}
              disabled={isCreating || isUpdating}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <Card
          heading={
            saveMode === "update"
              ? "Update Filter Preset"
              : "Save Filter Preset"
          }
        >
          {saveMode === "update" && saveName === currentPreset?.name && (
            <div className="preset-overwrite-warning">
              <strong>Overwriting:</strong>
              <span>
                This will update "{currentPreset?.name}". Change the name to
                save as a new preset.
              </span>
            </div>
          )}

          <TextInput
            name="preset-name"
            label="Preset Name"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="Preset Name"
            disabled={isCreating || isUpdating}
          />

          <TextInput
            name="preset-description"
            label="Description"
            value={saveDescription}
            onChange={(e) => setSaveDescription(e.target.value)}
            placeholder="Optional description of what this preset shows"
            disabled={isCreating || isUpdating}
          />

          <Select
            name="preset-scope"
            label="Visibility"
            options={scopeOptions}
            value={
              scopeOptions.find((option) => option.value === saveScope) || null
            }
            onChange={(option) => {
              if (
                option &&
                !Array.isArray(option) &&
                typeof option === "object" &&
                "value" in option
              ) {
                setSaveScope(option.value as SavedViewScope);
              }
            }}
            isDisabled={isCreating || isUpdating}
            placeholder="Select visibility..."
          />
        </Card>
      </KaboodleModal>

      {/* Load Preset Modal */}
      <KaboodleModal
        isOpen={showLoadModal}
        onClose={() => setShowLoadModal(false)}
        size="large"
      >
        <Card heading="Load Filter Preset">
          <div className="load-preset-list">
            {isLoadingSavedViews ? (
              <div className="loading-state">Loading presets...</div>
            ) : financePresets.length === 0 ? (
              <div className="empty-state">
                <span className="material-icons">bookmark_border</span>
                <p>No saved presets found.</p>
                <p>Save your current filters as a preset to get started.</p>
              </div>
            ) : (
              <div className="presets-grid">
                {financePresets.map((preset) => (
                  <div key={preset.id} className="preset-card">
                    <div className="preset-info">
                      <div className="preset-header">
                        <h4>{preset.name}</h4>
                        <div className="preset-meta">
                          <span
                            className="scope-badge"
                            data-scope={preset.scope}
                          >
                            {preset.scope === "USER"
                              ? "Personal"
                              : preset.scope_verbose}
                          </span>
                          <span className="created-by">
                            by {preset.created_by_name}
                          </span>
                        </div>
                      </div>

                      {preset.description && (
                        <p className="preset-description">
                          {preset.description}
                        </p>
                      )}

                      <div className="preset-date">
                        Created: {new Date(preset.created).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="preset-actions">
                      <Button
                        type="button"
                        onClick={() => {
                          onLoadPreset(preset);
                          setShowLoadModal(false);
                        }}
                        disabled={isDeleting}
                      >
                        <span className="material-icons">open_in_new</span>
                        Load
                      </Button>

                      {preset.scope === "ORGANIZATION" && (
                        <Button
                          type="button"
                          onClick={() => {
                            const fullUrl = new URL(
                              preset.url_path,
                              window.location.origin,
                            );
                            fullUrl.searchParams.set(
                              "preset",
                              preset.id.toString(),
                            );
                            const presetUrl = fullUrl.toString();

                            navigator.clipboard.writeText(presetUrl);
                            KaboodleToaster.success(
                              "Preset URL copied to clipboard",
                            );
                          }}
                          disabled={isDeleting}
                        >
                          <span className="material-icons">share</span>
                          Share
                        </Button>
                      )}

                      <Button
                        type="button"
                        onClick={() => handleDeletePreset(preset)}
                        disabled={isDeleting}
                        className="delete"
                      >
                        <span className="material-icons">delete</span>
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </KaboodleModal>

      {/* Delete confirmation modal */}
      <KaboodleModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        footer={
          <div className="flex jc-c">
            <Button
              type="button"
              onClick={confirmDeletePreset}
              disabled={isDeleting}
              className="delete"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
            <Button
              type="button"
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <Card heading="Delete Preset">
          <p>
            Are you sure you want to delete the preset "{presetToDelete?.name}"?
          </p>
          <p>This action cannot be undone.</p>
        </Card>
      </KaboodleModal>
    </>
  );
};

export default FinanceFilterPresetButtons;
